/**
 * Api — Dependency Container
 *
 * Centralises all SDK service clients behind a single injectable class.
 * The constructor receives an AxiosInstance (with auth interceptors already
 * attached) and the base URL, so the auth layer is never hard-coded.
 *
 * Pattern adapted from influenster-v4-mobile/src/api/Api.ts
 *
 * Usage:
 *   const api = new Api(authenticatedAxios, baseUrl);
 *   const campaigns = await api.campaigns.campaignsControllerGetUsersCampaignsList();
 */

import type { AxiosInstance } from 'axios';
import {
  CampaignsApi,
  MembersApi,
  SettingsApi,
} from '@bvengineering/cas-member-gateway-service-client';
import { ENV } from '@/config/env';

class Api {
  /** Campaign endpoints (list, detail, claim, opt-out, guidance) */
  public readonly campaigns: CampaignsApi;

  /** Member endpoints (create, info, update, picture) */
  public readonly members: MembersApi;

  /** Settings endpoints (social connections, addresses) — single shared instance */
  public readonly settings: SettingsApi;

  /** The base URL used for non-SDK endpoints (e.g. /invite) */
  public readonly basePath: string;

  /** The underlying axios instance (useful for raw requests like file uploads) */
  public readonly axios: AxiosInstance;

  constructor(
    axios: AxiosInstance,
    basePath: string = ENV.memberGatewayBasePath,
  ) {
    this.axios = axios;
    this.basePath = basePath;
    this.campaigns = new CampaignsApi(undefined, basePath, axios);
    this.members = new MembersApi(undefined, basePath, axios);
    this.settings = new SettingsApi(undefined, basePath, axios);
  }
}

export default Api;
export { Api };
